import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { formatDate } from '@platform/core/lib/format';
import { routes } from '@platform/core/lib/routes';
import type { IUserRef } from '@platform/core/types';
import { useRecordViewCount } from '@platform/engine/insights/useRecordViewCount';
import { useListRecordsQuery } from '@platform/state/api';
import { useAppSelector } from '@platform/state/hooks';
import { selectLang } from '@platform/state/selectors';
import { articleToc } from './articleToc';
import type { IArticleAsideProps } from './IArticleAsideProps';

/**
 * Бічна частина статті (прототип v4): «На цій сторінці» (заголовки), власник, дата перегляду й кількість переглядів (лічильник V010),
 * «Редагувати статтю», пов'язані статті тієї ж категорії; ширина — від роздільника (kbAsideW).
 */
export function ArticleAside({ ws, moduleKey, rt, record, onEdit, width }: IArticleAsideProps) {
  const { t } = useTranslation();
  const lang = useAppSelector(selectLang);
  const category = String(record.values['category'] ?? '');
  const { data } = useListRecordsQuery({ ws, rt: rt.key, params: { filter: `category:eq:${category}`, pageSize: 10 } }, { skip: !category });
  const related = (data?.items ?? []).filter((r) => r.id !== record.id).slice(0, 4);
  const toc = articleToc(String(record.values['body'] ?? ''));
  const owner = record.values['assignee'] as IUserRef | null | undefined;
  const review = typeof record.values['review'] === 'string' ? (record.values['review'] as string) : '';
  const views = useRecordViewCount(ws, rt.key, record.id);
  return (
    <aside className="rtab-art__aside" aria-label={t('recordPage.article.details')} style={width ? { width } : undefined}>
      {toc.length > 0 && (
        <section className="rtab-art__sec"><span className="rtab-art__head">{t('recordPage.article.toc')}</span>{toc.map((h, i) => <span key={i}>{h}</span>)}</section>
      )}
      <section className="rtab-art__sec">
        <span><span className="rtab-art__muted">{t('recordPage.article.owner')}:</span> {owner?.name ?? '—'}</span>
        <span><span className="rtab-art__muted">{t('recordPage.article.review')}:</span> {review ? formatDate(review, lang) : '—'}</span>
        {views !== undefined && <span><span className="rtab-art__muted">{t('insightBody.library.viewsLabel')}:</span> {views}</span>}
        {record.permissions?.edit !== false && <button type="button" className="rtab-btn rtab-btn--sm rtab-art__edit" onClick={onEdit}>{t('recordPage.article.edit')}</button>}
      </section>
      <section className="rtab-art__sec">
        <span className="rtab-art__head">{t('recordPage.article.related')}</span>
        {related.map((r) => <Link key={r.id} className="rtab-art__rel" to={routes.record(moduleKey, rt.key, r.id, 'read')}>{r.title}</Link>)}
      </section>
    </aside>
  );
}
